'use client';

import { useEffect, useState } from 'react';

import Link from 'next/link';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/utils/cn';
import { isUserAdmin } from '@/lib/admin';

import { ChevronRight, LayoutDashboard, LogOut, Settings2, TrendingUp } from 'lucide-react';
import type { User } from 'next-auth';

interface UserNavProps {
  user: User | null;
  onSignOut: () => void;
}

export default function UserNav({ user, onSignOut }: UserNavProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const isAdmin = isUserAdmin(user?.email);

  if (!user) {
    return (
      <div className='flex items-center gap-2'>
        <Link href='/auth/signin'>
          <button
            type='button'
            className='bg-brand-dark hover:bg-brand-dark/95 active:scale-95 rounded-full px-6 py-2.5 text-[11px] font-black tracking-[0.2em] text-white uppercase transition-all hover:shadow-lg'>
            Subscribe
          </button>
        </Link>
        <Link
          href='/auth/signin'
          className='hidden sm:block'>
          <button
            type='button'
            className='text-muted-foreground hover:text-foreground active:scale-95 px-5 py-2.5 text-[11px] font-black tracking-[0.2em] uppercase transition-all'>
            Sign in
          </button>
        </Link>
      </div>
    );
  }

  const displayName = user.name || user.email?.split('@')[0] || 'Researcher';
  const avatarUrl = user.image;

  if (!isMounted) {
    return (
      <div
        className={cn(
          'group border-border/10 text-foreground flex items-center space-x-2 rounded-full border py-1 pr-1.5 pl-1.5 text-sm font-bold opacity-50 sm:space-x-3 sm:py-1.5 sm:pr-4'
        )}>
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={displayName}
            className='size-7 rounded-full object-cover opacity-50 grayscale sm:h-8 sm:w-8'
            referrerPolicy='no-referrer'
          />
        ) : (
          <div className='bg-brand-dark/50 flex size-7 items-center justify-center rounded-full text-xs font-black text-white shadow-inner sm:h-8 sm:w-8'>
            {displayName.charAt(0).toUpperCase()}
          </div>
        )}
        <span className='hidden text-[13px] font-bold tracking-tight sm:inline'>
          {displayName}
        </span>
      </div>
    );
  }

  const baseUrl =
    process.env.NEXT_PUBLIC_SITE_URL || 'https://www.bioinfometrics.com';

  return (
    <DropdownMenu
      open={isDropdownOpen}
      onOpenChange={setIsDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          className={cn(
            'group border-border/10 text-foreground hover:bg-card hover:border-border/30 focus-visible:ring-primary/40 flex items-center space-x-2 rounded-full border py-1 pr-1.5 pl-1.5 text-sm font-bold transition-all duration-300 outline-none hover:shadow-xl focus-visible:ring-2 sm:space-x-3 sm:py-1.5 sm:pr-4',
            isDropdownOpen && 'bg-card border-border/40 shadow-xl'
          )}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={displayName}
              className='group-hover:ring-brand-dark/20 size-7 rounded-full object-cover ring-2 ring-transparent transition-all sm:h-8 sm:w-8'
              referrerPolicy='no-referrer'
            />
          ) : (
            <div className='bg-brand-dark flex size-7 items-center justify-center rounded-full text-xs font-black text-white shadow-inner sm:h-8 sm:w-8'>
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
          <span className='hidden text-[13px] font-bold tracking-tight sm:inline'>
            {displayName}
          </span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align='end'
        className='border-border/20 bg-card/95 animate-in fade-in zoom-in-95 z-[100] mt-3 w-64 rounded-[1.5rem] border p-2 shadow-2xl backdrop-blur-2xl duration-300'>
        <DropdownMenuLabel className='px-3 py-3 font-normal'>
          <div className='flex flex-col space-y-0.5'>
            <p className='text-foreground text-[14px] font-black tracking-tight'>
              {displayName}
            </p>
            <p className='text-muted-foreground truncate text-[10px] leading-none font-bold tracking-tight uppercase opacity-60'>
              {user?.email}
            </p>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator className='bg-border/10 mx-2' />

        <div className='space-y-0.5 p-1'>
          {/* Admin Only: Strategic Lab Terminal */}
          {isAdmin && (
            <Link
              href={`${baseUrl}/strategic-lab/partners`}
              onClick={() => setIsDropdownOpen(false)}>
              <DropdownMenuItem className='group/item focus:bg-brand-dark/10 focus:text-brand-dark dark:focus:bg-brand-dark-text-primary/10 dark:focus:text-brand-dark-text-primary group/item cursor-pointer gap-3 rounded-xl px-3 py-2 text-xs font-bold transition-all'>
                <div className='bg-muted/50 text-muted-foreground group-focus/item:bg-brand-dark/20 dark:group-focus/item:bg-brand-dark-text-primary/20 flex size-7 items-center justify-center rounded-lg transition-colors'>
                  <TrendingUp className='size-4' />
                </div>
                Researcher Radar
                <ChevronRight className='ml-auto size-3 opacity-0 transition-opacity group-focus/item:opacity-100' />
              </DropdownMenuItem>
            </Link>
          )}

          <Link
            href={`${baseUrl}/account`}
            onClick={() => setIsDropdownOpen(false)}>
            <DropdownMenuItem className='group/item focus:bg-brand-dark/10 focus:text-brand-dark dark:focus:bg-brand-dark-text-primary/10 dark:focus:text-brand-dark-text-primary group/item cursor-pointer gap-3 rounded-xl px-3 py-2 text-xs font-bold transition-all'>
              <div className='bg-muted/50 text-muted-foreground group-focus/item:bg-brand-dark/20 dark:group-focus/item:bg-brand-dark-text-primary/20 flex size-7 items-center justify-center rounded-lg transition-colors'>
                <LayoutDashboard className='size-4' />
              </div>
              Command Center
              <ChevronRight className='ml-auto size-3 opacity-0 transition-opacity group-focus/item:opacity-100' />
            </DropdownMenuItem>
          </Link>

          <Link
            href={`${baseUrl}/account/settings`}
            onClick={() => setIsDropdownOpen(false)}>
            <DropdownMenuItem className='group/item focus:bg-brand-dark/10 focus:text-brand-dark dark:focus:bg-brand-dark-text-primary/10 dark:focus:text-brand-dark-text-primary group/item cursor-pointer gap-3 rounded-xl px-3 py-2 text-xs font-bold transition-all'>
              <div className='bg-muted/50 text-muted-foreground group-focus/item:bg-brand-dark/20 dark:group-focus/item:bg-brand-dark-text-primary/20 flex size-7 items-center justify-center rounded-lg transition-colors'>
                <Settings2 className='size-4' />
              </div>
              Platform Settings
              <ChevronRight className='ml-auto size-3 opacity-0 transition-opacity group-focus/item:opacity-100' />
            </DropdownMenuItem>
          </Link>
        </div>

        <DropdownMenuSeparator className='bg-border/10 mx-2' />

        <div className='p-1'>
          <DropdownMenuItem
            className='group/logout text-destructive focus:bg-destructive/10 focus:text-destructive cursor-pointer gap-3 rounded-xl px-3 py-2 text-[10px] font-black tracking-wider whitespace-nowrap uppercase transition-all'
            onSelect={(e) => {
              e.preventDefault();
              setIsDropdownOpen(false);
              onSignOut();
            }}>
            <div className='bg-destructive/5 group-focus/logout:bg-destructive/10 flex size-7 items-center justify-center rounded-lg transition-colors'>
              <LogOut className='size-4 transition-transform group-hover/logout:translate-x-1' />
            </div>
            Log Out
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
